"use client";

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import OnboardingModal from "@/components/features/OnboardingModal";
import { Search, Filter, Briefcase } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import Job from "../components/Job";

interface JobData {
  id: string;
  title: string;
  company: string;
  location: string;
  working_hours: string;
  facility_type: string;
  description: string;
  requirements: string[];
  benefits: string[];
  created_at: string;
  company_id: string;
  is_active: boolean;
  salary_min?: number;
  salary_max?: number;
  contract_type?: string;
  company_description?: {
    de?: string;
    en?: string;
    es?: string;
  };
  available_to_talents: string[];
}

const CompleteProfileComponent = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["talent"]);
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">
        {t("talent:dashboard.complete_profile")}
      </h1>
      <p className="text-sm text-gray-500">
        {t("dashboard.complete_profile_description")}
      </p>
      <Button onClick={() => navigate("/profile")}>
        {t("talent:dashboard.complete_profile")}
      </Button>
    </div>
  );
};

export default function TalentJobs() {
  const { t } = useTranslation(["talent", "common"]);
  const { profile } = useAuth();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<JobData[]>([]);
  const [appliedJobIds, setAppliedJobIds] = useState<Map<string, string>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch applied job IDs for current user
  const fetchAppliedJobIds = useCallback(
    async (userId: string) => {
      try {
        setLoadingApplications(true);
        const { data: applications, error } = await supabase
          .from("talent_applications")
          .select("id, job_id")
          .eq("talent_id", userId);

        if (error) throw error;

        const appliedMap = new Map<string, string>();
        applications?.forEach((app) => {
          appliedMap.set(app.job_id, app.id);
        });
        setAppliedJobIds(appliedMap);
      } catch (error) {
        console.error("Error fetching applied job IDs:", error);
        toast({
          title: t("common:status.error"),
          description: t("talent:jobs.error_loading_applications"),
          variant: "destructive"
        });
      } finally {
        setLoadingApplications(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch active jobs from Supabase
        const { data: rawJobsData, error: jobsError } = await supabase
          .from("jobs")
          .select(
            `
            id,
            title,
            company,
            location,
            working_hours,
            facility_type,
            description,
            requirements,
            benefits,
            created_at,
            company_id,
            is_active,
            salary_min,
            salary_max,
            contract_type,
            available_to_talents
          `
          )
          .eq("is_active", true)
          .order("created_at", { ascending: false });

        if (jobsError) {
          console.error("Error fetching jobs:", jobsError);
          throw jobsError;
        }
        // Remove jobs that are not available to the user.
        const jobsData = (rawJobsData || []).filter((job) => {
          return job.available_to_talents?.includes(profile?.id || "");
        });
        // Fetch company descriptions for each job
        const jobsWithDescriptions = await Promise.all(
          jobsData.map(async (job) => {
            try {
              const { data: companyData, error: companyError } = await supabase
                .from("companies")
                .select("description")
                .eq("id", job.company_id)
                .single();
              if (companyError) {
                console.warn(
                  `Error fetching company description for ${job.company_id}:`,
                  companyError
                );
                return job; // Return job without description if fetch fails
              }

              return {
                ...job,
                company_description: companyData?.description || null
              };
            } catch (error) {
              console.warn(
                `Error processing company description for ${job.company_id}:`,
                error
              );
              return job; // Return job without description if processing fails
            }
          })
        );

        setJobs(jobsWithDescriptions);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError(t("talent:jobs.error_loading_jobs"));
        toast({
          title: t("common:status.error"),
          description: t("talent:jobs.error_loading_jobs"),
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchData = async () => {
      await fetchJobs();
      if (!profile.anonymous) {
        await fetchAppliedJobIds(profile.id);
      }
    };

    fetchData();
  }, [toast, t, profile, fetchAppliedJobIds]);

  const filteredJobs = jobs.filter(
    (job) =>
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const handleApply = async (jobId: string, companyId: string) => {
    if (!profile) {
      toast({
        title: t("common:status.error"),
        description: "Please log in to apply for jobs.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: newApplication, error } = await supabase
        .from("talent_applications")
        .insert({
          talent_id: profile.id,
          company_id: companyId,
          job_id: jobId,
          partner_id: profile.partner_id
        })
        .select()
        .single();
      if (error) throw error;

      // Add the job ID to applied jobs map
      setAppliedJobIds((prev) => {
        const newMap = new Map(prev);
        newMap.set(jobId, newApplication.id);
        return newMap;
      });

      toast({
        title: t("common:status.success"),
        description: t("talent:jobs.apply_success_message", { jobId })
      });
    } catch (error) {
      console.error("Error applying for job:", error);
      toast({
        title: t("common:status.error"),
        description: "Failed to apply for job. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              {t("common:buttons.retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OnboardingModal />
      {/* TODO: Add logic to check if profile is complete */}
      <CompleteProfileComponent />
      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t("talent:jobs.search_placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {t("talent:jobs.filters.title")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Job Results */}
      <div className="space-y-4">
        {filteredJobs.length > 0 ? (
          filteredJobs.map((job) => (
            <Job
              key={job.id}
              job={job}
              talentAppliedForJob={appliedJobIds.has(job.id)}
              applicationId={appliedJobIds.get(job.id)}
              loading={loadingApplications}
              onApply={handleApply}
            />
          ))
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("talent:jobs.no_results")}
              </h3>
              <p className="text-gray-600">
                {t("talent:jobs.no_results_description")}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
